import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* ─── Contact Hero Background ─── */
const CONTACT_HERO_BG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20golden%20network%20connections%20and%20nodes%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20lines%20luxurious%20atmosphere%20no%20text&width=1920&height=800&seq=contacthero1&orientation=landscape';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (options: {
          portalId: string;
          formId: string;
          region: string;
          target: string;
        }) => void;
      };
    };
  }
}

export default function ContactPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };

  /* Scroll reveal */
  const { ref: formRef, visible: formVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: infoRef, visible: infoVisible } = useScrollReveal({ threshold: 0.1 });

  useSEO({
    title: t('seo.contactTitle'),
    description: t('seo.contactDescription'),
    keywords: t('seo.contactKeywords'),
    path: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(t('seo.contactTitle'), t('seo.contactDescription'), '/contact', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('nav.contact'), path: '/contact' },
        ]),
      ],
    },
  });

  useEffect(() => {
    const containerId = 'hubspot-form-container';
    const existing = document.getElementById(containerId);
    if (existing) existing.innerHTML = '';

    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      if (window.hbspt) {
        const formId = i18n.language === 'ja'
          ? '473fb36d-3c67-4980-9aa4-afe8db4d9fd4'
          : '638b69f3-c803-40d6-bbcf-8665e4351bb6';
        window.hbspt.forms.create({
          portalId: '39621570',
          formId,
          region: 'na1',
          target: `#${containerId}`,
        });
      }
    };

    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, [i18n.language]);

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      <ElegantHero
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
        tag="Contact"
        backgroundImage={CONTACT_HERO_BG}
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('nav.contact') },
        ]}
      />

      <main className="py-20 md:py-28 px-6 md:px-10 max-w-[800px] mx-auto">
        <div
          ref={formRef as React.RefObject<HTMLDivElement>}
          style={{
            opacity: formVisible ? 1 : 0,
            transform: formVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div
            className="p-8 md:p-12 border"
            style={{
              borderColor: 'rgba(212, 207, 196, 0.5)',
              background: '#FFFFFF',
            }}
          >
            <h2
              className="text-xl md:text-2xl mb-8 text-center"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
                fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
              }}
            >
              {t('contact.sendMessage')}
            </h2>
            <div id="hubspot-form-container" />
          </div>
        </div>
      </main>

      <ElegantFooter />
    </div>
  );
}
